import * as React from 'react'
import Box from '@mui/material/Box'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TablePagination from '@mui/material/TablePagination'
import TableRow from '@mui/material/TableRow'
import Paper from '@mui/material/Paper'
import {
  Button,
  Container,
  Grid,
  InputBase,
  Link,
  Stack,
  TableHead,
} from '@mui/material'
import IconButton from '@mui/material/IconButton'
import MenuIcon from '@mui/icons-material/Menu'
import SearchIcon from '@mui/icons-material/Search'

import { useEffect, useMemo, useState } from 'react'
import jwt_decode from 'jwt-decode'
import SkeletonTableLoader from '../../../common/components/loaders/skeletonLoader'
import { useLocation, useSearchParams } from 'react-router-dom'
import LoadingSection from '../../../common/components/loaders/loader'
import NoDataFoundCard from '../../../common/components/cards/noDataFoundCard'
import { invoiceBillingListGet } from '../../../api/get/getApi'
import { InvoiceDetails } from './InvoiceDetails'

import DownloadIcon from '@mui/icons-material/Download'
import { createRef } from 'react'
import {
  formatDate,
  getCompanyLevel,
  getComparator,
  normalizeApiResponse,
  stableSort,
} from '../../../helpers/HelperFunctions'
import { useToastContext } from '../../../App'
import FilterListIcon from '@mui/icons-material/FilterList'
import TableSortLabel from '@mui/material/TableSortLabel'
import Chip from '@mui/material/Chip'
import useTableFilters from '../../../common/components/tableFilter/useTableFilters'
import ColumnFilter from '../../../common/components/tableFilter/ColumnFilter'
import GlobalSearch from '../../../common/components/tableFilter/GlobalSearch'
// ──────────────────────────────────────────────────────────────
// Table Column configuration
// ──────────────────────────────────────────────────────────────
const tableColumns = [
  {
    key: 'billId',
    label: 'Bill #',
    getValue: (row) => row.billId,
    getLabel: (row) => row.billId?.toString() || '',
  },
  {
    key: 'dueDate',
    label: 'Due Date',
    getValue: (row) => row.dueDate,
    getLabel: (row) =>
      row.dueDate ? formatDate(new Date(row.dueDate)) : '',
  },
  {
    key: 'invoiceAmount',
    label: 'Total',
    getValue: (row) => row.invoiceAmount,
    getLabel: (row) =>
      row.invoiceAmount != null ? String(row.invoiceAmount) : '',
  },
  {
    key: 'remainingDue',
    label: 'Remaining Due',
    getValue: (row) => row.remainingDue,
    getLabel: (row) =>
      row.remainingDue != null ? String(row.remainingDue) : '',
  },
]

function InvoiceList(props) {
  const { toast } = useToastContext()
  const [page, setPage] = React.useState(0)
  const [rowsPerPage, setRowsPerPage] = React.useState(5)
  const [rowsList, setRowsList] = React.useState([])
  const [rowsFilteredList, setRowsFilteredList] = React.useState([])
  const [edited, setEdited] = React.useState('')
  const [details, setDetails] = React.useState('')
  const [showDetails, setShowDetails] = React.useState(true)
  const [loader, setLoader] = React.useState(false)
  const [submitLoader, setSubmitLoader] = React.useState(false)
  const [searchParams] = useSearchParams()
  const [state, setState] = React.useState({
    right: false,
  })
  const pageTopRef = createRef()
  const location = useLocation()
  const [isOpen, setIsOpen] = useState(false)
  const [order, setOrder] = React.useState('asc')
  const [orderBy, setOrderBy] = React.useState()
  const renderBillDueAmount = (row) => {
    switch (row.billPaymentStatus?.toLowerCase()) {
      case 'overdue':
        return row.amountDue
      case 'not paid':
        return row.amountDue
      case 'paid':
        return '0'
      default:
        return row.amountDue
    }
  }
  const mappedRows = (rowsList || []).map((row) => ({
    ...row,
    dueDateLabel: formatDate(new Date(row.dueDate)),
    remainingDue: renderBillDueAmount(row),
  }))
  const {
    searchTerm,
    setSearchTerm,
    filteredData,
    uniqueValues,
    filterAnchorEl,
    setFilterAnchorEl,
    currentColumn,
    openFilter,
    closeFilter,
    toggleFilter,
    clearColumnFilter,
    isFilterActive,
    filters,
  } = useTableFilters(mappedRows, tableColumns);
  const currentLabel = tableColumns.find((c) => c.key === currentColumn)?.label || currentColumn || '';
  const sortedRows = React.useMemo(() => {
    return stableSort(filteredData, getComparator(order, orderBy));
  }, [filteredData, order, orderBy]);
  let inputHandler = (e) => {
    const lowerCase = e.target.value
    const filteredData = rowsFilteredList.filter((el) => {
      if (lowerCase === '') {
        return el
      } else {
        return el?.billId.toString().includes(lowerCase)
      }
    })
    setRowsList(filteredData)
    setPage(0)
  }
  const getList = (id) => {
    setLoader(true)
    invoiceBillingListGet(
      id,
      getCompanyLevel(
        searchParams.get('company_id'),
        location.pathname.slice(1)
      )
    )
      .then((response) => {
        setLoader(false)
        setRowsList(response.data.result)
      })
      .catch((error) => {
        setLoader(false)
        toast.showToast(normalizeApiResponse(error)?.message, 'error')
      })
  }
  const getDecodeValue = () => {
    const decodeToken = localStorage.getItem('sd_cp.jwt:tkn')
    const decoded = jwt_decode(decodeToken)
    if (decoded) {
      let companyId = searchParams.get('company_id')
      if (!companyId) {
        companyId = decoded?.company_id
      }
      getList(companyId)
    }
  }

  const handleChangePage = (event, newPage) => {
    setPage(newPage)
    pageTopRef.current.scrollTop = 0
  }

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(Number(event.target.value))
    setPage(0)
    pageTopRef.current.scrollTop = 0
  }

  const getDetails = (details) => {
    setDetails(details)
    setShowDetails(false)
  }
  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rowsList.length) : 0

  // const tableData = useMemo(()=> {
  //     return props?.array || []
  // }, [props?.array])

  useEffect(() => {
    getDecodeValue()
  }, [])



  return (
    <Container maxWidth="xl" className="Dash_main_container">
      {submitLoader ? <LoadingSection></LoadingSection> : ''}
      <Grid item xs={12}>
        {showDetails ? (
          <Box>
            <Box
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '0.5em',
                marginTop: '0.5em',
              }}
            >
              <GlobalSearch
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value.toLowerCase())}
                placeholder="Search Invoices"
              />
            </Box>
            <TableContainer component={Paper} ref={pageTopRef}>
              <Table
                stickyHeader
                sx={{ minWidth: 500 }}
                aria-label="custom pagination table"
              >
                <TableHead className="thead">
                  <TableRow>
                    {tableColumns.map((col) => (
                      <TableCell key={col.key}>
                        <Box style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <TableSortLabel
                            active={orderBy === col.key}
                            direction={orderBy === col.key ? order : 'asc'}
                          // onClick={(e) => handleRequestSort(e, col.key)}
                          >{col.label}</TableSortLabel>
                          <IconButton
                            size="small"
                            onClick={(e) => openFilter(e, col.key)}
                            color={isFilterActive(col.key) ? 'primary' : 'default'}
                          >
                            <FilterListIcon fontSize="small" />
                          </IconButton>
                          {isFilterActive(col.key) && (
                            <Chip size="small"
                              label={(filters?.[col.key]?.length || 0)}
                              //  label={filters[col.key]?.length  || 0} 
                              color="primary" />
                          )}
                        </Box>
                      </TableCell>
                    ))}
                    {/* <TableCell>Bill #</TableCell>
                    <TableCell>Due Date</TableCell>
                    <TableCell>Total</TableCell>
                    <TableCell>Remaining Due</TableCell> */}
                  </TableRow>
                </TableHead>
                {loader ? (
                  <TableBody className="tbody">
                    <TableRow style={{ height: 53 * emptyRows }}>
                      <TableCell colSpan={6}>
                        <SkeletonTableLoader></SkeletonTableLoader>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                ) : (
                  <TableBody className="tbody">
                    {(rowsPerPage > 0
                      ? (filteredData || []).slice(
                        page * rowsPerPage,
                        page * rowsPerPage + rowsPerPage
                      )
                      : filteredData || []
                    ).map((row, index) => (
                      <TableRow hover key={index}>
                        <TableCell scope="row" onClick={() => getDetails(row)}>
                          <Link> {row.billId}</Link>
                        </TableCell>
                        <TableCell>
                          {formatDate(new Date(row.dueDate))}
                        </TableCell>
                        <TableCell>{row.invoiceAmount}</TableCell>
                        <TableCell>{renderBillDueAmount(row)}</TableCell>
                      </TableRow>
                    ))}

                    {emptyRows > 0 && (
                      <TableRow style={{ height: 53 * emptyRows }}>
                        <TableCell colSpan={6} />
                      </TableRow>
                    )}
                    {filteredData.length === 0 ? (
                      <TableRow style={{ height: 53 * emptyRows }}>
                        <TableCell colSpan={6} style={{ border: 'none' }}>
                          <h3
                            style={{
                              margin: '0px auto',
                              padding: '80px 0',

                              textAlign: 'center',
                            }}
                          >
                            <NoDataFoundCard
                              messages="No Invoice Found"
                              borderNone={true}
                            />
                          </h3>
                        </TableCell>
                      </TableRow>
                    ) : (
                      ''
                    )}
                  </TableBody>
                )}
              </Table>
            </TableContainer>
            <Stack direction="row" justifyContent="space-between">
              {!loader && (
                <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-start', px: 2 }}>
                  <Chip
                    label={
                      <>
                        <strong>{filteredData.length}</strong> / {rowsList.length} records
                      </>
                    }
                    color={filteredData.length < rowsList.length ? 'primary' : 'default'}
                    variant="outlined"
                    size="small"
                  />
                  {(filters && Object.keys(filters).length > 0 || searchTerm.trim()) && (
                    <Chip
                      label="Filtered"
                      color="secondary"
                      size="small"
                      sx={{ ml: 1 }}
                    />
                  )}
                </Box>
              )}
              <TablePagination
                rowsPerPageOptions={[5, 10, 25, { label: 'All', value: -1 }]}
                component="div"
                count={filteredData?.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </Stack>
            <ColumnFilter
              anchorEl={filterAnchorEl}
              onClose={closeFilter}
              columnLabel={currentLabel}
              uniqueValues={uniqueValues[currentColumn] || []}
              selectedValues={filters ? filters[currentColumn] : []}
              onToggleValue={toggleFilter}
              onClear={clearColumnFilter}
            />
          </Box>
        ) : (
          <InvoiceDetails
            setShowDetails={setShowDetails}
            details={details}
            billId={details?.billId}
            handleBack={() => {
              setShowDetails(true)
              getDecodeValue()
            }}
          />
        )}
      </Grid>
    </Container>
  )
}

export default InvoiceList
